<template>
  <div class="pricing-section">
    <div class="pricing-cards">
      <div
        v-for="plan in featurePlans"
        :key="plan.name"
        class="pricing-card"
        :class="{
          popular: highlightPlan.includes(plan.name),
        }"
      >
        <div v-if="highlightPlan.includes(plan.name)" class="popular-badge">
          Most Popular
        </div>
        <h3>{{ $t(plan.name) }}</h3>
        <div class="price">
          <span class="amount">{{ plan.price_per_month }} EUR</span>
          <span class="period"
            >{{ $t("per_month") }}/ {{ $t("per_user") }}</span
          >
        </div>

        <p v-if="plan.name === 'free'" class="plan-subtitle">
          {{ $t("for_starters") }}
        </p>
        <p v-else-if="plan.name === 'plus'" class="plan-subtitle">
          {{ $t("for_freelancers") }}
        </p>
        <p v-else-if="plan.name === 'premium'" class="plan-subtitle">
          {{ $t("for_businesses") }}
        </p>

        <ul class="pricing-features">
          <li v-for="(feature, index) in plan.features" :key="index">
            <i :class="feature.included ? 'fa fa-check' : 'fa fa-times'"></i>
            {{ $t(feature.name) }}
          </li>
        </ul>

        <p class="min-duration">
          {{ $t("minimum_duration") }}: {{ plan.period_duration / 30 }}
          {{ $t("months") }}
        </p>

        <NuxtLink
          to="/contact"
          v-if="showCTAButton"
          :class="
            highlightPlan.includes(plan.name)
              ? 'btn btn-primary'
              : 'btn btn-outline'
          "
        >
          {{ $t("website.contact_us") }}
        </NuxtLink>
      </div>
    </div>

    <div
      v-if="showOnlyHighlighted && showAllPricingPlansButton"
      class="pricing-link"
    >
      <NuxtLink to="/pricing">
        {{ $t("show_all_plans") }}
      </NuxtLink>
    </div>

    <div v-if="showOnlyHighlighted && featurePlans.length === 0">
      <p>{{ $t("no_plans_found") }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { plans, billing_modules } from "@/utils/billingHelpers";

const props = defineProps({
  highlightPlan: {
    type: Array<"free" | "plus" | "premium">,
    required: false,
    default: () => [],
  },
  showOnlyHighlighted: {
    type: Boolean,
    default: false,
  },
  scaleHighlitedPlan: {
    type: Boolean,
    default: true,
  },
  showCTAButton: {
    type: Boolean,
    default: true,
  },
  showAllPricingPlansButton: {
    type: Boolean,
    default: true,
  },
});

const featurePlans = computed(() => {
  let plansCopy = JSON.parse(JSON.stringify(subscriptionTypes.value));
  const billingModulesCopy = JSON.parse(JSON.stringify(billing_modules));

  // Remove billing modules that are not displayed on the pricing page
  for (const key in billingModulesCopy) {
    if (!billingModulesCopy[key].isDisplayedOnPricingPage) {
      delete billingModulesCopy[key];
    }
  }

  // Sort features by amount of values (included plans) they have in descending order
  const sortedBillingModules = Object.values(billingModulesCopy).sort(
    (a: any, b: any) => b.values.length - a.values.length,
  );

  for (const key in plansCopy) {
    const plan = plansCopy[key];

    for (const key in sortedBillingModules) {
      const billingModule = sortedBillingModules[key] as any;
      plan.features = plan.features || [];
      if (billingModule.values.includes(plan.name)) {
        plan.features.push({
          name: billingModule.title,
          included: true,
        });
      } else {
        plan.features.push({
          name: billingModule.title,
          included: false,
        });
      }
    }
  }

  // If only highlighted plans should be shown, filter out the rest
  if (props.showOnlyHighlighted) {
    plansCopy = plansCopy.filter((plan: any) =>
      props.highlightPlan.includes(plan.name),
    );
  }

  return plansCopy;
});

// Fetch subscription types
import { useBillingStore } from "@/store/billing.store";
import type { components } from "~/types_backend";
const billingStore = useBillingStore();
const subscriptionTypes = ref([]) as Ref<
  components["schemas"]["SubscriptionType"][]
>;
const fetchSubscriptionTypes = async () => {
  const fetchedST = (await billingStore.getSubscriptionTypes()).results;

  // Combine subscription types with plans
  const plansCopy = JSON.parse(JSON.stringify(plans));
  for (const key in plansCopy) {
    const plan = plansCopy[key];
    const subscriptionType = fetchedST.find((st) => st.name === plan.name);
    if (subscriptionType) {
      plan.price_per_month = subscriptionType.price_per_month;
      plan.period_duration = subscriptionType.period_duration;
    }
  }

  subscriptionTypes.value = plansCopy.filter(
    (plan: any) => !plan.show_on_pricing_page,
  );
};

onMounted(() => {
  fetchSubscriptionTypes();
});
</script>

<style scoped>
.pricing-section {
  width: 100%;
}

.pricing-cards {
  width: 100%;
  margin: 40px 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.pricing-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 15px;
  padding: 40px;
  position: relative;
  transition: all 0.3s ease;
}

.pricing-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.pricing-card.popular {
  border-color: #ff6b35;
  border-width: 3px;
}

.popular-badge {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

.pricing-card h3 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 20px;
}

.price {
  margin-bottom: 15px;
}

.amount {
  font-size: 2.5rem;
  font-weight: 800;
  color: #ff6b35;
  display: block;
}

.period {
  font-size: 0.9rem;
  color: #6b7280;
  display: block;
}

.plan-subtitle {
  color: #6b7280;
  font-weight: 600;
  margin-bottom: 30px;
}

.pricing-features {
  list-style: none;
  padding: 0;
  margin-bottom: 30px;
  text-align: left;
}

.pricing-features li {
  padding: 10px 0;
  color: #4b5563;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 1.1rem;
}

.pricing-features i {
  color: #10b981;
  margin-top: 3px;
}

.pricing-features .fa-times {
  color: #ef4444;
}

.min-duration {
  color: #6b7280;
  font-size: 0.9rem;
  margin-bottom: 25px;
}

.btn {
  padding: 14px 32px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  display: inline-block;
  transition: all 0.3s ease;
  cursor: pointer;
  text-align: center;
  width: 100%;
}

.btn-primary {
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(255, 107, 53, 0.4);
}

.btn-outline {
  background: transparent;
  border: 2px solid #ff6b35;
  color: #ff6b35;
}

.btn-outline:hover {
  background: #ff6b35;
  color: white;
}

.pricing-link {
  margin-top: 40px;
}

.pricing-link a {
  color: #ff6b35;
  font-weight: 600;
  text-decoration: none;
  border-bottom: 2px solid #ff6b35;
  padding-bottom: 2px;
}

@media (max-width: 1024px) {
  .pricing-cards {
    grid-template-columns: 1fr;
    max-width: 500px;
  }
}
</style>
